import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiDownload, FiPlus, FiTrash2, FiUpload } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { settingsAPI } from '../../services/api';
import { useTranslation } from 'react-i18next';

const MAX_ACTIVE = 2;

const JobDocumentsSettings = () => {
  const { t } = useTranslation();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [name, setName] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const activeDocs = useMemo(
    () => (docs || []).filter((d) => String(d?.status || '').toLowerCase() === 'active'),
    [docs]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await settingsAPI.getJobDocuments();
      setDocs(res.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || t('common.failed'));
      setDocs([]);
    }
    setLoading(false);
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const onUpload = async () => {
    const trimmed = String(name || '').trim();
    if (!trimmed) return toast.error('Document name is required');
    if (!file) return toast.error('Please choose a file');
    if (activeDocs.length >= MAX_ACTIVE) return toast.error('Only two active documents are allowed');

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('document_name', trimmed);
      fd.append('file', file);
      await settingsAPI.uploadJobDocument(fd);
      toast.success('Document uploaded');
      setName('');
      setFile(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('common.failed'));
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (doc) => {
    if (!doc?.job_document_id) return;
    if (!window.confirm(`Delete "${doc.document_name}"?`)) return;
    try {
      await settingsAPI.deleteJobDocument(doc.job_document_id);
      toast.success('Deleted');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('common.failed'));
    }
  };

  const onDownload = (doc) => {
    if (!doc?.job_document_id) return;
    window.open(settingsAPI.downloadJobDocumentUrl(doc.job_document_id), '_blank', 'noopener,noreferrer');
  };

  if (loading) return <div className="loading-spinner"><div className="spinner"></div></div>;

  return (
    <div className="card" style={{ padding: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ marginTop: 0, marginBottom: 6 }}>France Job Documents</h3>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Upload exactly two documents here. They will appear as a download dropdown beside “Add Job”.
          </div>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          Active: <strong>{activeDocs.length}</strong> / {MAX_ACTIVE}
        </div>
      </div>

      <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, alignItems: 'end' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Document name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Job Contract Template" />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>File</label>
            <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </div>
          <button type="button" className="btn btn-primary" onClick={onUpload} disabled={uploading || activeDocs.length >= MAX_ACTIVE}>
            <FiUpload /> {uploading ? t('common.uploading', 'Uploading...') : 'Upload'}
          </button>
        </div>

        <div className="table-responsive">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>File</th>
                <th>Status</th>
                <th style={{ width: 140 }}>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {(docs || []).map((d) => (
                <tr key={d.job_document_id}>
                  <td style={{ fontWeight: 600 }}>{d.document_name || '-'}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{d.original_filename || '-'}</td>
                  <td>{d.status || '-'}</td>
                  <td>
                    <div className="table-actions">
                      <button className="view" title="Download" onClick={() => onDownload(d)}>
                        <FiDownload />
                      </button>
                      <button className="delete" title={t('common.delete')} onClick={() => onDelete(d)}>
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {docs.length === 0 && (
                <tr>
                  <td colSpan="4" className="text-center" style={{ padding: 20, color: 'var(--text-muted)' }}>
                    No documents uploaded yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default JobDocumentsSettings;

